import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { HiArrowRight } from 'react-icons/hi';
import { AiOutlinePlus } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/reducers';
import { store } from '../../../redux/store';
import {
  setSelectedCommunity,
  setSelectedDashboard,
  wipeSelectedCommunity,
} from '../../../redux/slices/applicationSlice';
import { setConnections } from '../../../redux/slices/connectionsSlice';
import ApiImage from '../../components/misc/ApiImage';
import { communityTypeIcons } from '../../../utils/iconUtils';
import Icon from '../../components/icons/Icon';
import Community, { CommunityRole } from '../../../types/community';
import WarningModal from '../../components/modals/WarningModal';
import communityService from '../../../services/community/communityService';

export default function CommunityOverviewPage(): JSX.Element {
  const { t } = useTranslation();
  const communities = useSelector((state: RootState) => state.application.communities);
  const businesses = useSelector((state: RootState) => state.application.businesses);
  const mainCommunityIds = new Set<number>(
    (businesses ?? [])
      .filter((b) => b.community && b.community.id !== undefined)
      .map((b) => b.community!.id),
  );
  const navigate = useNavigate();

  useEffect(() => {
    communityService.fetchCommunity();
    store.dispatch(wipeSelectedCommunity());
    store.dispatch(setConnections([]));
  }, []);

  return (
    <>
      <div className="mb-2 ml-2">
        <h1 className="text-3xl font-semibold">{t('page.communityOverview.title')}</h1>
        <p className="py-1">{t('page.communityOverview.subtitle')}</p>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:gap-8">
        {/* Activated */}
        {communities
          ?.filter(
            (community) =>
              !mainCommunityIds.has(community.id) &&
              community.dashboard &&
              community.role === CommunityRole.ADMIN,
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((community, index) => (
            <CommunityCard key={community.id ?? `activated-${index}`} community={community} />
          ))}
        {/* Non-activated */}
        {communities
          ?.filter(
            (community) =>
              !mainCommunityIds.has(community.id) &&
              !community.dashboard &&
              community.role === CommunityRole.ADMIN,
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((community, index) => (
            <CommunityCard key={community.id ?? `nonactivated-${index}`} community={community} />
          ))}
        <div
          onClick={() => navigate('/onboarding')}
          className="group relative w-full cursor-pointer content-center items-center rounded-lg border-2 border-dashed border-primary-900 hover:border-opacity-80 hover:bg-primary-300 hover:bg-opacity-10 sm:w-48">
          <div className="text-md mx-auto flex flex-col items-center font-medium text-primary-900 group-hover:text-opacity-80">
            <AiOutlinePlus className="h-20 w-20 stroke-1" />
            {t('page.communityOverview.new')}
          </div>
        </div>
      </div>
    </>
  );
}

interface CommunityCardProps {
  community: Community;
}

function CommunityCard({ community }: CommunityCardProps): JSX.Element {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = React.useState(false);
  const { t } = useTranslation();

  const handleCommunitySelect = (communityId: number, dashboardId: number) => {
    store.dispatch(setSelectedCommunity(communityId));
    store.dispatch(setSelectedDashboard(dashboardId));

    navigate('/overview');
  };

  const handleClick = () => {
    if (community.dashboard) {
      handleCommunitySelect(community.id!, community.dashboard!.dashboardId);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <>
      <div
        onClick={handleClick}
        className={`group relative w-full cursor-pointer rounded-lg border border-primary-900 transition-all hover:bg-primary-300 hover:bg-opacity-10 sm:w-48 ${!community.dashboard && 'opacity-30'}`}>
        {community.picture ? (
          <ApiImage
            alt="community-profile"
            path={community.picture}
            className="h-48 w-full rounded-t-lg object-cover"
          />
        ) : (
          <div className="mx-auto flex h-48 w-full items-center rounded-t-lg text-primary-900">
            <Icon
              className="mx-auto my-auto h-20 w-20 text-primary-900"
              icon={communityTypeIcons[community.type]}
            />
          </div>
        )}

        <div className="wrap flex w-full flex-col flex-wrap justify-between gap-2 border-t border-primary-900 p-4 py-3">
          <p className="w-full overflow-hidden break-words font-medium">{community.name}</p>
          <button type="button" className="bg-vera-green-lightest w-fit rounded-lg p-1">
            <HiArrowRight className="mx-auto h-6 w-6 text-black" />
          </button>
        </div>
      </div>
      {!community.dashboard && (
        <WarningModal
          open={modalOpen}
          setOpen={setModalOpen}
          title={t('component.modal.activateDashboard.title', { name: community.name })}
          description={t('component.modal.activateDashboard.subtitle')}
          button={t('component.modal.activateDashboard.button')}
          onContinue={() => navigate('/dashboard/activate', { state: { community } })}
        />
      )}
    </>
  );
}
